// Build a Stripe Connect `appearance` config from our shadcn HSL CSS custom
// properties so the embedded iframe blends with the surrounding card.
//
// Why we go through a probe element instead of reading the bare HSL value
// and wrapping it in `hsl(...)`: our tokens use the modern space-separated
// syntax (`--foreground: 0 0% 98%`). Stripe's iframe accepts hex / rgb /
// hsl strings, but in dark mode a `hsl(0 0% 98%)` value was observed to be
// silently dropped by Stripe's parser (likely the no-comma form), at which
// point Stripe falls back to its defaults — light text on light defaults
// over our dark `colorBackground`, hence the "dim grey on near-black"
// rendering. The probe lets the browser normalize whatever color expression
// the project uses today (`hsl(...)`, `oklch(...)`, etc.) into a plain
// `rgb(...)` string that every parser handles.
//
// Stripe only re-reads `appearance` on instance creation — to react to a
// runtime theme switch, remount the panel (we key on `resolvedTheme` in
// `<StripeEmbeddedPage>`). See `.tondocs/ui/THEMING.md` for the token model.

type Vars = Record<string, string>;

export function buildStripeAppearance():
  | { overlays: "dialog"; variables: Vars }
  | undefined {
  if (typeof window === "undefined") return undefined;

  // Connect panels render on ordinary (non-forced-dark) pages, so the probe
  // follows the page theme.
  const probe = createProbe("inherit");
  const color = (cssVar: string, fallback: string): string =>
    resolveColor(probe, `hsl(var(${cssVar}))`, fallback);

  const variables: Vars = {
    // — typography —
    fontFamily:
      "var(--font-sans, system-ui), -apple-system, 'Segoe UI', Roboto, sans-serif",
    fontSizeBase: "14px",
    // Stripe clamps spacingUnit to 8–20px — values outside that range are
    // silently ignored and the iframe falls back to its default (~3px),
    // which is what produced the "everything is glued to the edge" look.
    spacingUnit: "10px",

    // — geometry —
    borderRadius: "8px",
    buttonBorderRadius: "8px",
    formBorderRadius: "6px",
    badgeBorderRadius: "6px",
    overlayBorderRadius: "12px",

    // — control padding (extra breathing room on inputs and table rows) —
    inputFieldPaddingX: "12px",
    inputFieldPaddingY: "10px",
    tableRowPaddingY: "12px",
    buttonPaddingX: "14px",
    buttonPaddingY: "8px",

    // — surfaces —
    colorBackground: color("--background", "#ffffff"),
    offsetBackgroundColor: color("--muted", "#f4f4f5"),
    formBackgroundColor: color("--background", "#ffffff"),

    // — text —
    colorText: color("--foreground", "#0a0a0a"),
    colorSecondaryText: color("--muted-foreground", "#6b7280"),
    formPlaceholderTextColor: color("--muted-foreground", "#9ca3af"),

    // — accents —
    colorPrimary: color("--primary", "#0a0a0a"),
    colorDanger: color("--destructive", "#ef4444"),
    colorBorder: color("--border", "#e5e7eb"),
    formHighlightColorBorder: color("--ring", "#0a0a0a"),
    formAccentColor: color("--primary", "#0a0a0a"),

    // — primary buttons (e.g. "Save", "Continue") —
    buttonPrimaryColorBackground: color("--primary", "#0a0a0a"),
    buttonPrimaryColorBorder: color("--primary", "#0a0a0a"),
    buttonPrimaryColorText: color("--primary-foreground", "#ffffff"),

    // — secondary buttons (e.g. "Edit", "Export") —
    buttonSecondaryColorBackground: color("--secondary", "#f4f4f5"),
    buttonSecondaryColorBorder: color("--border", "#e5e7eb"),
    buttonSecondaryColorText: color("--secondary-foreground", "#0a0a0a"),

    // — destructive buttons (e.g. dispute / cancel) —
    buttonDangerColorBackground: color("--destructive", "#ef4444"),
    buttonDangerColorBorder: color("--destructive", "#ef4444"),
    buttonDangerColorText: color("--destructive-foreground", "#ffffff"),

    // — links inside Stripe's body copy —
    actionPrimaryColorText: color("--primary", "#2563eb"),
    actionSecondaryColorText: color("--muted-foreground", "#6b7280"),
  };

  destroyProbe(probe);

  return {
    overlays: "dialog",
    variables,
  };
}

// Build a Stripe Elements `Appearance` config (from `@stripe/stripe-js`) for
// `<CheckoutElementsProvider>` — the successor to `buildStripeAppearance`
// above, but NOT a drop-in replacement. Connect embedded panels
// (`AppearanceOptions`, used by `buildStripeAppearance`) and
// Elements/Checkout Sessions (`Appearance`, used here) are different SDK
// products with disjoint variable vocabularies (e.g. `formPlaceholderTextColor`
// vs `colorTextPlaceholder`, `overlays: 'dialog'` has no Elements equivalent)
// — they cannot share one variable map.
//
// Reuses the same probe pattern as `buildStripeAppearance` (see file-header
// docstring) to work around Stripe's parser silently dropping our
// space-separated `hsl(0 0% 98%)` tokens: the probe lets the browser
// normalize whatever color expression the project uses into a plain
// `rgb(...)` string every Stripe parser accepts.
//
// Paired factory for `<CheckoutElementsProvider>` in
// `components/checkout/stripe-embedded-checkout-inline.tsx`. Fed to the
// provider under `options.elementsOptions.appearance` (NESTED path — that
// is the Elements SDK convention, distinct from the flat top-level
// `options.appearance` that CheckoutFormProvider expected).
//
// `mode` is the theme the SURROUNDING SUBTREE renders in — not necessarily
// `next-themes`' `resolvedTheme`. A forced-dark surface (the mobile checkout
// sheet) is dark while `<html>` is `.light`, and the probe must read the same
// token set that surface uses, or Stripe gets `theme: 'night'` with white
// `colorBackground`/`colorText` variables layered on top — variables win, so
// the iframe painted white cards inside the dark sheet. Scoping the probe to
// `.dark` is a no-op when the page itself is dark (identical values); it only
// bites in the forced-dark-inside-light case.
//
// @see .tondocs/stripe/CHECKOUT_FORM_PROVIDER_MIGRATION.md
// @see .tondocs/ui/THEMING.md § "The forced-dark card pattern"
export function buildCheckoutFormAppearance(
  mode: "dark" | "light" | undefined,
): import("@stripe/stripe-js").Appearance | undefined {
  if (typeof window === "undefined") return undefined;

  const probe = createProbe(mode === "dark" ? "dark" : "inherit");
  const color = (cssVar: string, fallback: string): string =>
    resolveColor(probe, `hsl(var(${cssVar}))`, fallback);

  const appearance: import("@stripe/stripe-js").Appearance = {
    theme: mode === "dark" ? "night" : "stripe",
    variables: {
      fontFamily:
        "var(--font-sans, system-ui), -apple-system, 'Segoe UI', Roboto, sans-serif",
      // Mobile-input floor — see .tondocs/ui/MOBILE_INPUTS.md (text-sm
      // triggers iOS Safari auto-zoom on focus).
      fontSizeBase: "16px",
      // Elements default (unlike Connect's 8-20px clamp above).
      spacingUnit: "4px",
      borderRadius: "8px",
      // Apple Pay / Google Pay buttons in <ExpressCheckoutElement> do not
      // always inherit the general `borderRadius` — brand guidelines require
      // Stripe to gate wallet button geometry behind an explicit knob. Match
      // the sibling PayPal buttons (`--paypal-button-border-radius: 30px` in
      // `components/cart/paypal-checkout.tsx`) so both rails read as the
      // same UI system.
      buttonExpressCheckoutBorderRadius: "30px",

      colorPrimary: color("--primary", "#0a0a0a"),
      colorBackground: color("--background", "#ffffff"),
      colorText: color("--foreground", "#0a0a0a"),
      colorDanger: color("--destructive", "#ef4444"),
      colorTextSecondary: color("--muted-foreground", "#6b7280"),
      colorTextPlaceholder: color("--muted-foreground", "#9ca3af"),
      accessibleColorOnColorPrimary: color("--primary-foreground", "#ffffff"),
    },
  };

  destroyProbe(probe);

  return appearance;
}

// `scope` picks WHICH token set the probe reads. `"inherit"` hangs the probe
// off `document.body` bare, so it resolves against whatever `next-themes` put
// on `<html>`. `"dark"` tags the probe `.dark`, making `hsl(var(--background))`
// &co. resolve against the `.dark` block in `app/globals.css` regardless of the
// page theme — that block is a plain class selector re-declaring both the bare
// vars and the `--color-*` tokens (PR #152), so a scoped `.dark` works on any
// element, including one with no `.dark` ancestor.
function createProbe(scope: "inherit" | "dark"): HTMLDivElement {
  const probe = document.createElement("div");
  if (scope === "dark") probe.className = "dark";
  probe.setAttribute("aria-hidden", "true");
  probe.style.position = "absolute";
  probe.style.width = "0";
  probe.style.height = "0";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  document.body.appendChild(probe);
  return probe;
}

function destroyProbe(probe: HTMLDivElement): void {
  probe.parentNode?.removeChild(probe);
}

function resolveColor(
  probe: HTMLDivElement,
  expression: string,
  fallback: string,
): string {
  probe.style.color = "";
  probe.style.color = expression;
  const computed = getComputedStyle(probe).color;
  // If the expression couldn't resolve (CSS var missing, syntax unsupported)
  // computed.color is an empty string or the previous value. Browsers
  // normally normalize to "rgb(R, G, B)" / "rgba(...)" — treat anything
  // else as a miss and use the fallback.
  if (!computed || !computed.startsWith("rgb")) return fallback;
  return computed;
}
